import isEmpty from 'lodash/isEmpty';

import {
    ReferralData,
    ReferralStatus,
    ReferralUpdateForm,
    ReferralCreateData,
    ReferralCreateForm,
    UpdateQuery,
    ReferralListResponse,
    ReferralPlanParams,
    ReferralGetListQuery,
    EhrsReferralResponse,
    CalculateCostResponse,
    CalculateCostParams,
} from 'chaika-frontend-types/frontend-cms';

import { axios } from '@lib/axios';

import { removeNilKeys } from '@utils/removeNiKeys';

const BASE_URL = '/api/ehr';

export interface ReferralCreateParams extends Omit<ReferralCreateData, 'serviceParams'> {
    id: string;
    serviceId: string;
    parentEntryId?: string;
    plans?: ReferralPlanParams[];
}

export interface ReferralUpdateParams extends Omit<ReferralUpdateForm, 'serviceParams'> {
    isComposite?: boolean;
    plans?: ReferralPlanParams[];
}

export class ReferralApi {
    public static async create(ehrId: string, referrals: ReferralCreateParams[]): Promise<void> {
        const params = this.mapReferralCreateParams(referrals);
        await axios.post(`${BASE_URL}/${ehrId}/referral`, params);
    }

    public static async createList(ehrId: string, params: ReferralCreateParams[]): Promise<void> {
        const referrals = params.map(referral => this.mapReferralCreateParams([referral]));
        await axios.post(`${BASE_URL}/${ehrId}/referral/list`, { referrals });
    }

    public static async update(
        ehrId: string,
        params: UpdateQuery,
        { referringDoctorId, ...referral }: ReferralUpdateForm,
    ): Promise<void> {
        await axios.put(
            `${BASE_URL}/${ehrId}/referral`,
            {
                referral: {
                    ...referral,
                    referringDoctorId: !!referringDoctorId && referringDoctorId !== '' ? referringDoctorId : undefined,
                },
            },
            { params },
        );
    }

    public static async updateStatus(ehrId: string, params: UpdateQuery, status: ReferralStatus): Promise<void> {
        await axios.put(`${BASE_URL}/${ehrId}/referral/status`, { status }, { params });
    }

    public static async getEhrsReferral(ehrIds: string[], statuses: ReferralStatus[]): Promise<EhrsReferralResponse> {
        const res = await axios.get<EhrsReferralResponse>(`/api/referral/ehrs`, {
            params: { statuses, ehrIds: ehrIds.join(',') },
        });

        return res.data;
    }

    public static async getList(params?: ReferralGetListQuery): Promise<ReferralData[]> {
        const res = await axios.get<ReferralListResponse>(`/api/referral`, { params });

        return res.data.referrals;
    }

    public static async getListWithCount(params?: ReferralGetListQuery): Promise<ReferralListResponse> {
        const res = await axios.get<ReferralListResponse>(`/api/referral`, { params });

        return res.data;
    }

    public static async addCoAuthor(
        ehrId: string,
        referralIds: string[],
        coAuthorId: string,
        entryId?: string,
    ): Promise<void> {
        await axios.post(
            `${BASE_URL}/${ehrId}/referral/co-author`,
            { referral: { coAuthorId } },
            { params: { entryId, ids: referralIds.join(',') } },
        );
    }

    public static async removeCoAuthor(
        ehrId: string,
        referralIds: string[],
        coAuthorId: string,
        entryId?: string,
    ): Promise<void> {
        await axios.delete(`${BASE_URL}/${ehrId}/referral/co-author`, {
            params: { entryId, ids: referralIds.join(',') },
            data: { referral: { coAuthorId } },
        });
    }

    public static async getReferralMinMaxPrices(
        ehrId: string,
        params: CalculateCostParams,
    ): Promise<CalculateCostResponse[]> {
        const res = await axios.post(`${BASE_URL}/${ehrId}/referral/prices`, params);

        return res.data.prices;
    }

    private static mapReferralCreateParams(referrals: ReferralCreateParams[]): ReferralCreateForm {
        return {
            ids: referrals.map(({ id }) => id),
            referral: removeNilKeys(this.toReferralCreateForm(referrals[0])),
        };
    }

    private static toReferralCreateForm({
        serviceId,
        compositeServices,
        plans,
        parentEntryId,
        entryId,
        id,
        serviceParams: labServiceParams,
        ...referral
    }: ReferralCreateParams & Partial<ReferralCreateData>): Omit<ReferralCreateData, 'id'> {
        let serviceParams = {};

        if (plans || labServiceParams) {
            serviceParams = compositeServices
                ? {
                      compositeServices,
                  }
                : {
                      serviceParams: labServiceParams
                          ? {
                                plans,
                                ...labServiceParams,
                            }
                          : {
                                plans,
                                id: serviceId,
                            },
                  };
        }

        return {
            serviceId,
            entryId: parentEntryId ?? (!!entryId && entryId !== '' ? entryId : undefined),
            compositeServices: isEmpty(serviceParams) ? compositeServices : undefined,
            ...referral,
            ...serviceParams,
        };
    }
}
